import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Copy, Eye, EyeOff, Loader2, RefreshCw, X } from 'lucide-react';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
  Switch,
} from '@scio3/ui';
import type { ShareLink } from '@scio3/shared-types';
import { useShareLink } from '../hooks/useShareLink';
import { DateTimePicker } from '@/components/shared/DateTimePicker';
import { toast } from 'sonner';

type Props = {
  shareLink: ShareLink;
  onUpdate: (
    _id: string,
    payload: {
      passwordEnabled?: boolean;
      expirationEnabled?: boolean;
      expirationValue?: Date;
    },
  ) => Promise<void>;
};

function generatePassword(length = 12): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function ShareLinkAdvancedSection({ shareLink, onUpdate }: Props) {
  const { t } = useTranslation();
  const { setPassword, revealPassword } = useShareLink(shareLink.objectId);

  const [isOpen, setIsOpen] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);

  // Expiration — local state (ISO string) to avoid API call on each picker interaction
  const expirationEnabled = shareLink.expiration?.enabled ?? false;
  const serverExpirationIso = shareLink.expiration?.value
    ? new Date(shareLink.expiration.value).toISOString()
    : '';
  const [localExpiration, setLocalExpiration] = useState(serverExpirationIso);
  const expirationDirty = localExpiration !== serverExpirationIso;

  // Sync local state when server data changes (e.g., after toggle sets default +30 days)
  useEffect(() => {
    setLocalExpiration(serverExpirationIso);
  }, [serverExpirationIso]);

  const handlePasswordToggle = useCallback(
    async (enabled: boolean) => {
      await onUpdate(shareLink._id, { passwordEnabled: enabled });
    },
    [shareLink._id, onUpdate],
  );

  const handleCreatePassword = useCallback(() => {
    const pwd = generatePassword();
    setPasswordValue(pwd);
    setShowPassword(true);
  }, []);

  const handleSavePassword = useCallback(async () => {
    if (!passwordValue.trim()) return;
    setPasswordLoading(true);
    try {
      await setPassword(shareLink._id, passwordValue.trim());
      setPasswordValue('');
      setShowPassword(false);
      setRevealedPassword(null);
      // toast handled by useShareLink hook
    } catch {
      toast.error(t('Failed to update password'));
    } finally {
      setPasswordLoading(false);
    }
  }, [passwordValue, shareLink._id, setPassword, t]);

  const handleRevealPassword = useCallback(async () => {
    setPasswordLoading(true);
    try {
      const result = await revealPassword(shareLink._id);
      if (result?.password) {
        setRevealedPassword(result.password);
      } else {
        toast.info(result?.message ?? t('No password set'));
      }
    } catch {
      toast.error(t('Failed to reveal password'));
    } finally {
      setPasswordLoading(false);
    }
  }, [shareLink._id, revealPassword, t]);

  const handleExpirationToggle = useCallback(
    async (enabled: boolean) => {
      const payload: { expirationEnabled: boolean; expirationValue?: Date } = {
        expirationEnabled: enabled,
      };
      if (enabled && !shareLink.expiration?.value) {
        // Default: 30 days from now
        const defaultDate = new Date();
        defaultDate.setDate(defaultDate.getDate() + 30);
        payload.expirationValue = defaultDate;
      }
      await onUpdate(shareLink._id, payload);
    },
    [shareLink._id, shareLink.expiration?.value, onUpdate],
  );

  const handleExpirationApply = useCallback(async () => {
    if (!localExpiration) return;
    await onUpdate(shareLink._id, {
      expirationEnabled: true,
      expirationValue: new Date(localExpiration),
    });
  }, [shareLink._id, localExpiration, onUpdate]);

  const passwordEnabled = shareLink.password?.enabled ?? false;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 py-1 text-sm font-medium cursor-pointer hover:text-foreground transition-colors text-muted-foreground">
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {t('Advanced')}
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-4 pt-2">
        {/* Password */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>{t('Enable Access Password')}</Label>
            <Switch
              checked={passwordEnabled}
              onCheckedChange={handlePasswordToggle}
              aria-label={t('Enable Access Password')}
              className="cursor-pointer"
            />
          </div>

          {passwordEnabled && (
            <div className="space-y-2 pl-1">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('Enter password')}
                    value={passwordValue}
                    onChange={(e) => setPasswordValue(e.target.value)}
                    autoComplete="new-password"
                    aria-label={t('Access password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                    aria-label={showPassword ? t('Hide password') : t('Show password')}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCreatePassword}
                  className="cursor-pointer"
                >
                  <RefreshCw className="mr-1 h-3 w-3" />
                  {t('Create Password')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRevealPassword}
                  disabled={passwordLoading}
                  className="cursor-pointer"
                >
                  {t('Reveal Password')}
                </Button>
                {passwordValue && (
                  <Button
                    size="sm"
                    onClick={handleSavePassword}
                    disabled={passwordLoading}
                    className="cursor-pointer"
                  >
                    {passwordLoading && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    {t('Update')}
                  </Button>
                )}
              </div>
              {revealedPassword && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">{t('Current Password')}</Label>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      className="cursor-pointer"
                      onClick={() => setRevealedPassword(null)}
                      aria-label={t('Close')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 rounded-md bg-muted p-2 text-sm font-mono">
                      {revealedPassword}
                    </div>
                    <Button
                      variant="outline"
                      size="icon-sm"
                      className="shrink-0 cursor-pointer"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(revealedPassword);
                          toast.success(t('Copied to clipboard'));
                        } catch {
                          toast.error(t('Failed to copy'));
                        }
                      }}
                      aria-label={t('Copy password')}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Expiration */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>{t('Enable Expiration')}</Label>
            <Switch
              checked={expirationEnabled}
              onCheckedChange={handleExpirationToggle}
              aria-label={t('Enable Expiration')}
              className="cursor-pointer"
            />
          </div>

          {expirationEnabled && (
            <div className="flex items-end gap-2 pl-1">
              <div className="flex-1">
                <DateTimePicker
                  label={t('Expiration date and time')}
                  value={localExpiration}
                  onChange={setLocalExpiration}
                  utc={false}
                  placeholder={t('Pick date & time')}
                  timeLabel={t('Time')}
                />
              </div>
              {expirationDirty && (
                <Button
                  size="sm"
                  onClick={handleExpirationApply}
                  className="cursor-pointer shrink-0"
                >
                  {t('Apply')}
                </Button>
              )}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
